import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Badge, Tooltip } from '@plerionhq/design-system';
import {
  resolveTrustState,
  trustStateDescription,
  trustStateDisplay,
} from '../label-maps';
import { formatReviewDate } from '../lib/review-date';
import type { AccessGrant } from '../types';
import { useTranslation } from 'react-i18next';

// U+00A0 non-breaking space, built via fromCharCode so the source stays plain
// ASCII. Same reason as ScopeBadge: the Badge label sets its own `text-wrap`, so
// in the grants table's auto-layout "Needs re-review" would break across lines.
const NON_BREAKING_SPACE = String.fromCharCode(0x00a0);

type TrustBadgeProperties = {
  grant: Pick<AccessGrant, 'trustStatus' | 'trustedUntil' | 'trustLapseReason'>;
};

/**
 * A grant's trust, told apart by source rather than by `trustStatus` alone.
 *
 * The two states worth distinguishing are a reviewer's time-boxed trust — which
 * shows its end date, because it is temporary and the reader needs to know when
 * it lapses — and "Needs re-review", which is the one that means someone widened
 * the grant after it was trusted.
 */
export function TrustBadge({ grant }: TrustBadgeProperties) {
  const { t } = useTranslation('accessGrants');
  const state = resolveTrustState(grant);
  const { label, status } = trustStateDisplay(t)[state];
  return (
    <Tooltip label={trustStateDescription(t, grant)}>
      <Badge
        label={label.replace(/ /g, NON_BREAKING_SPACE)}
        status={status}
        secondaryLabel={
          state === 'trusted-until' && grant.trustedUntil
            ? t('untilDate', {
                date: formatReviewDate(grant.trustedUntil),
              }).replace(/ /g, NON_BREAKING_SPACE)
            : undefined
        }
        icon={
          state === 'needs-re-review' ? <ExclamationTriangleIcon /> : undefined
        }
      />
    </Tooltip>
  );
}
